import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, CardHeader, Nav } from "react-bootstrap";
import PaginationControls from "../../components/PaginationControls";
import { buildNewsMeta, buildSnippet, CATEGORY_DEFINITIONS } from "../../utils/homeUtils";

const ITEMS_PER_PAGE = 5;

const buildSections = (items) => {
  const newsItems = Array.isArray(items) ? items : [];
  const normalizedItems = newsItems
    .map((item) => buildNewsMeta(item))
    .sort((a, b) => b.timestamp - a.timestamp);

  return CATEGORY_DEFINITIONS.map((category) => ({
    key: category.key,
    label: category.label,
    items: normalizedItems.filter((item) => item.categoryKey === category.key),
  }));
};

const getDefaultTab = (items) => {
  return buildSections(items).find((section) => section.items.length > 0)?.key || CATEGORY_DEFINITIONS[0].key;
};

export default function HomeCategorySection({ data }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => getDefaultTab(data));
  const [hasUserSelectedTab, setHasUserSelectedTab] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const sections = useMemo(() => buildSections(data), [data]);
  const activeSection = sections.find((section) => section.key === activeTab) || sections[0];
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((activeSection?.items?.length || 0) / ITEMS_PER_PAGE)),
    [activeSection]
  );
  const paginatedItems = useMemo(() => {
    const items = activeSection?.items || [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [activeSection, currentPage]);

  useEffect(() => {
    if (hasUserSelectedTab) {
      return;
    }
    
    setActiveTab(getDefaultTab(data));
  }, [data, hasUserSelectedTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleNewsItemClick = (item) => {
    if (item?.documentid) {
      navigate(`/news/${item.documentid}`, {
        state: {
          newsItem: item,
        },
      });
      return;
    }

    if (item?.link) {
      window.open(item.link, "_blank", "noopener,noreferrer");
      return;
    }

    if (item?.ticker) {
      navigate(`/stock/${item.ticker}`);
    }
  };
  
  return (
    <Row>
      <Col>
        <Card>
          <CardHeader className="border-0 bg-transparent pb-0">
            <div className="home-section-header">
              <div>
                <div className="home-section-title">카테고리별 주요 뉴스</div>
                <div className="home-section-subtitle">최근 공시와 뉴스를 유형별로 빠르게 확인합니다.</div>
              </div>
              <button type="button" className="home-section-link" onClick={() => {navigate("/news")}}>
                더보기
              </button>
            </div>
          </CardHeader>
          <CardBody>
            <Nav className="nav-line mb-3 flex-nowrap overflow-auto nav-scroll-hide align-items-center">
              {sections.map((section) => {
                const isActive = activeTab === section.key;

                return (
                  <Nav.Link
                    key={section.key}
                    as="button"
                    type="button"
                    className={isActive ? "active border-bottom" : ""}
                    active={isActive}
                    onClick={() => {
                      setHasUserSelectedTab(true);
                      setActiveTab(section.key);
                    }}
                  >
                    {section.label}
                  </Nav.Link>
                );
              })}
              <span className="home-category-count ms-auto flex-shrink-0">
                {activeSection?.items?.length || 0}건
              </span>
            </Nav>

            {activeSection?.items?.length ? (
              <div>
                {paginatedItems.map((item, index) => (
                  <Card
                    key={`${activeSection.key}-${currentPage}-${index}`}
                    className="mb-2"
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNewsItemClick(item)}
                    >
                    <CardHeader className="bg-transparent">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <div
                          className="fw-semibold text-truncate"
                          style={{ fontSize: "1rem" }}
                        >
                          {item.title}
                        </div>
                        <div className="text-secondary fs-xs flex-shrink-0">{item.dateLabel}</div>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-2">
                      <div
                        className="text-secondary"
                        style={{
                          fontSize: "0.95rem",
                          lineHeight: 1.55,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {buildSnippet(item.summary, 130)}
                      </div>
                    </CardBody>
                  </Card>
                ))}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrev={() => setCurrentPage((page) => page - 1)}
                  onNext={() => setCurrentPage((page) => page + 1)}
                  className="mt-3"
                />
              </div>
            ) : (
              <div 
                className="d-flex align-items-center justify-content-center text-center w-100" 
                style={{
                  padding: "24px",
                  borderRadius: "0.5rem"
                }}>표시할 뉴스가 없습니다.</div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}
